// src/graphql/normalizers/jobNormalizer.js
// Job enum normalization utilities
//
// These utilities normalize job type and status values from the backend
// to a consistent UPPER_CASE format used in the frontend.

/**
 * Special case mappings for job types that have alternative names
 * Most job types can be normalized by simply converting to UPPER_CASE
 */
const JOB_TYPE_ALIASES = {
    'assess': 'ASSESS_TENDER', // Legacy alias
};

/**
 * Normalize job type to uppercase enum format
 * Handles both snake_case from backend and existing UPPER_CASE values
 *
 * @param {string} type - Job type in any format (snake_case, UPPER_CASE, etc.)
 * @returns {string} Normalized job type in UPPER_CASE format
 */
export const normalizeJobType = (type) => {
    if (!type) return type;

    const lowerType = type.toLowerCase();

    // Check for special aliases first
    if (JOB_TYPE_ALIASES[lowerType]) {
        return JOB_TYPE_ALIASES[lowerType];
    }

    // Generic normalization: snake_case -> UPPER_CASE
    return type.toUpperCase().replace(/-/g, '_');
};

/**
 * Convert UPPER_CASE job type to snake_case for backend
 *
 * @param {string} type - Job type in UPPER_CASE format
 * @returns {string} Job type in snake_case format for backend
 */
export const denormalizeJobType = (type) => {
    if (!type) return type;
    return type.toLowerCase();
};

/**
 * Normalize job status to uppercase enum format
 *
 * @param {string} status - Job status in any case
 * @returns {string} Normalized status in UPPER_CASE format
 */
export const normalizeJobStatus = (status) => {
    if (!status) return status;
    return status.toUpperCase();
};

/**
 * Normalize a single job object
 *
 * @param {Object} job - Job object from GraphQL response
 * @returns {Object} Normalized job object
 */
export const normalizeJob = (job) => {
    if (!job) return job;
    return {
        ...job,
        type: normalizeJobType(job.type),
        status: normalizeJobStatus(job.status),
    };
};

/**
 * Normalize an array of job objects
 *
 * @param {Array} jobs - Array of job objects
 * @returns {Array} Array of normalized job objects
 */
export const normalizeJobArray = (jobs) => {
    if (!Array.isArray(jobs)) return jobs;
    return jobs.map(normalizeJob);
};

/**
 * Normalize jobs data from GraphQL response
 * Handles various response shapes (adminJobs, jobs, etc.)
 *
 * @param {Object} data - GraphQL response data
 * @returns {Object} Normalized response data
 */
export const normalizeJobsData = (data) => {
    if (!data) return data;

    const normalizedData = { ...data };

    // Handle adminJobs response
    if (normalizedData.adminJobs) {
        normalizedData.adminJobs = normalizeJobArray(normalizedData.adminJobs);
    }

    // Handle jobs response
    if (normalizedData.jobs) {
        normalizedData.jobs = normalizeJobArray(normalizedData.jobs);
    }

    // Handle stale jobs response
    if (normalizedData.adminGetStaleJobs?.jobs) {
        normalizedData.adminGetStaleJobs.jobs = normalizeJobArray(normalizedData.adminGetStaleJobs.jobs);
    }

    return normalizedData;
};

/**
 * Convert job type to a human-readable label
 *
 * @param {string} type - Job type in any format
 * @returns {string} Human-readable label
 */
export const jobTypeToLabel = (type) => {
    if (!type) return '';
    // Convert UPPER_CASE or snake_case to Title Case
    return type
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
